import { useState } from "react";
import { PlayCircle } from "lucide-react";

/**
 * Lazy-mounted YouTube embed. First paint shows a static thumbnail so the
 * page weight stays light; iframe only mounts on user click. Uses the
 * privacy-friendly youtube-nocookie domain.
 *
 * Used by:
 *   • Homepage "Coming Soon" trailer band (ComingSoonTrailer.jsx)
 *   • /watch video library (Watch.jsx)
 */
export default function YouTubeFrame({ videoId, title, testid }) {
  const [mounted, setMounted] = useState(false);
  const thumb = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;

  return (
    <div
      data-testid={testid || `youtube-frame-${videoId}`}
      className="relative w-full aspect-video overflow-hidden rounded-2xl bg-black shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] ring-1 ring-gold/15"
    >
      {mounted ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1`}
          title={title}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      ) : (
        <button
          type="button"
          aria-label={title}
          onClick={() => setMounted(true)}
          className="group absolute inset-0 w-full h-full focus:outline-none"
        >
          <img
            src={thumb}
            alt={title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
            onError={(e) => {
              e.currentTarget.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden
            style={{
              background:
                "linear-gradient(135deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.10) 50%, rgba(0,0,0,0.45) 100%)",
            }}
          />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-black/55 border border-gold/70 backdrop-blur-sm group-hover:bg-gold/20 group-hover:border-gold transition-all duration-500">
              <PlayCircle size={40} strokeWidth={1.25} className="text-gold" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
